import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  DocumentDuplicateIcon, 
  PlayIcon,
  PencilIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';

interface OperationTemplate {
  id: string;
  name: string;
  operation: 'feeding';
  defaultValues: any;
  applyToStacks: number[];
  schedule?: {
    frequency: 'daily' | 'weekly' | 'custom';
    time?: string;
    daysOfWeek?: number[];
  };
  usageCount: number;
  lastUsed?: Date;
  isFavorite: boolean;
}

interface OperationTemplatesProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyTemplate: (template: OperationTemplate) => void;
  onCreateTemplate: (template: Omit<OperationTemplate, 'id' | 'usageCount' | 'lastUsed'>) => void;
  stacks: Array<{ id: number; name: string; status: string }>;
}

const OperationTemplates: React.FC<OperationTemplatesProps> = ({
  isOpen,
  onClose,
  onApplyTemplate,
  onCreateTemplate,
  stacks
}) => {
  const [templates, setTemplates] = useState<OperationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<OperationTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Partial<OperationTemplate>>({});

  // Предустановленные шаблоны
  const defaultTemplates: OperationTemplate[] = [
    {
      id: 'morning_feeding',
      name: 'Утреннее кормление',
      operation: 'feeding',
      defaultValues: {
        feedTypeId: 1, // Отруби
        amount: 1.5,
        activity: 'medium'
      },
      applyToStacks: stacks.filter(s => s.status === 'active').map(s => s.id),
      schedule: {
        frequency: 'daily',
        time: '09:00'
      },
      usageCount: 15,
      lastUsed: new Date(Date.now() - 86400000), // вчера
      isFavorite: true
    }
  ];

  useEffect(() => {
    // Загружаем шаблоны из localStorage или используем по умолчанию
    const savedTemplates = localStorage.getItem('wormtracker_templates');
    if (savedTemplates) {
      try {
        const parsed = JSON.parse(savedTemplates);
        setTemplates(parsed.map((t: any) => ({
          ...t,
          lastUsed: t.lastUsed ? new Date(t.lastUsed) : undefined
        })));
      } catch (e) {
        setTemplates(defaultTemplates);
      }
    } else {
      setTemplates(defaultTemplates);
    }
  }, []);

  useEffect(() => {
    // Сохраняем шаблоны в localStorage
    localStorage.setItem('wormtracker_templates', JSON.stringify(templates));
  }, [templates]);

  const handleApplyTemplate = (template: OperationTemplate) => {
    // Обновляем статистику использования
    const updatedTemplates = templates.map(t => 
      t.id === template.id 
        ? { ...t, usageCount: t.usageCount + 1, lastUsed: new Date() }
        : t
    );
    setTemplates(updatedTemplates);
    
    onApplyTemplate(template);
  };

  const handleCreateTemplate = () => {
    if (!editingTemplate.name || !editingTemplate.operation) return;

    const newTemplate: OperationTemplate = {
      id: `custom_${Date.now()}`,
      name: editingTemplate.name,
      operation: editingTemplate.operation,
      defaultValues: editingTemplate.defaultValues || {},
      applyToStacks: editingTemplate.applyToStacks || [],
      schedule: editingTemplate.schedule,
      usageCount: 0,
      isFavorite: false
    };

    setTemplates([...templates, newTemplate]);
    onCreateTemplate(newTemplate);
    setIsCreating(false);
    setEditingTemplate({});
  };

  const handleDeleteTemplate = (id: string) => {
    if (confirm('Удалить этот шаблон?')) {
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  const handleDuplicateTemplate = (template: OperationTemplate) => {
    const newTemplate: OperationTemplate = {
      ...template,
      id: `copy_${Date.now()}`,
      name: `${template.name} (копия)`,
      usageCount: 0,
      lastUsed: undefined,
      isFavorite: false
    };
    
    setTemplates([...templates, newTemplate]);
  };

  const toggleFavorite = (id: string) => {
    setTemplates(templates.map(t => 
      t.id === id ? { ...t, isFavorite: !t.isFavorite } : t
    ));
  };

  const getOperationIcon = (operation: string) => {
    switch (operation) {
      case 'feeding': return '🥗';
      default: return '📋';
    }
  };

  const getOperationName = (operation: string) => {
    switch (operation) {
      case 'feeding': return 'Кормление';
      default: return 'Операция';
    }
  };

  const formatLastUsed = (date?: Date) => {
    if (!date) return 'Никогда';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Только что';
    if (diffHours < 24) return `${diffHours} ч. назад`;
    if (diffDays === 1) return 'Вчера';
    if (diffDays < 7) return `${diffDays} дн. назад`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  const sortedTemplates = [...templates].sort((a, b) => {
    // Сначала избранные
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    
    // Затем по частоте использования
    return b.usageCount - a.usageCount;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
        
        {/* Заголовок */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">📋 Шаблоны операций</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Создать</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex h-[600px]">
          {/* Список шаблонов */}
          <div className="w-2/3 border-r border-gray-200 overflow-y-auto">
            <div className="p-4 space-y-3">
              {sortedTemplates.map((template) => (
                <div
                  key={template.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{getOperationIcon(template.operation)}</span>
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        {template.isFavorite && (
                          <StarIcon className="w-4 h-4 text-yellow-500 fill-current" />
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Тип: {getOperationName(template.operation)}</p>
                        <p>Стеллажей: {template.applyToStacks.length}</p>
                        <p>Использований: {template.usageCount}</p>
                        <p>Последний раз: {formatLastUsed(template.lastUsed)}</p>
                        
                        {template.schedule && (
                          <div className="flex items-center space-x-1 text-xs text-blue-600">
                            <ClockIcon className="w-3 h-3" />
                            <span>
                              {template.schedule.frequency === 'daily' && `Ежедневно в ${template.schedule.time}`}
                              {template.schedule.frequency === 'weekly' && 'Еженедельно'}
                              {template.schedule.frequency === 'custom' && 'По расписанию'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(template.id);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={template.isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
                      >
                        <StarIcon className={`w-4 h-4 ${
                          template.isFavorite 
                            ? 'text-yellow-500 fill-current' 
                            : 'text-gray-400'
                        }`} />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApplyTemplate(template);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Применить шаблон"
                      >
                        <PlayIcon className="w-4 h-4 text-green-500" />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateTemplate(template);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Дублировать"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4 text-blue-500" />
                      </button>
                      
                      {!template.id.startsWith('morning_') && !template.id.startsWith('evening_') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template.id);
                          }}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Удалить"
                        >
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Детали шаблона или форма создания */}
          <div className="w-1/3 p-4 overflow-y-auto">
            {isCreating ? (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Новый шаблон</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Название
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.name || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Мой шаблон"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Тип операции
                  </label>
                  <select
                    value={editingTemplate.operation || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, operation: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Выберите тип</option>
                    <option value="feeding">Кормление</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Применять к стеллажам
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {stacks.filter(s => s.status === 'active').map((stack) => (
                      <label key={stack.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingTemplate.applyToStacks?.includes(stack.id) || false}
                          onChange={(e) => {
                            const currentStacks = editingTemplate.applyToStacks || [];
                            const newStacks = e.target.checked
                              ? [...currentStacks, stack.id]
                              : currentStacks.filter(id => id !== stack.id);
                            setEditingTemplate({ ...editingTemplate, applyToStacks: newStacks });
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{stack.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateTemplate}
                    disabled={!editingTemplate.name || !editingTemplate.operation}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 transition-colors"
                  >
                    Создать
                  </button>
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setEditingTemplate({});
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : selectedTemplate ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {getOperationIcon(selectedTemplate.operation)} {selectedTemplate.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getOperationName(selectedTemplate.operation)}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Значения по умолчанию:</h4>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(selectedTemplate.defaultValues, null, 2)}
                    </pre>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Стеллажи ({selectedTemplate.applyToStacks.length}):
                  </h4>
                  <div className="text-sm text-gray-600">
                    {selectedTemplate.applyToStacks.map(stackId => {
                      const stack = stacks.find(s => s.id === stackId);
                      return stack?.name;
                    }).filter(Boolean).join(', ')}
                  </div>
                </div>

                {selectedTemplate.schedule && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Расписание:</h4>
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="w-4 h-4" />
                        <span>
                          {selectedTemplate.schedule.frequency === 'daily' && `Ежедневно в ${selectedTemplate.schedule.time}`}
                          {selectedTemplate.schedule.frequency === 'weekly' && 'Еженедельно'}
                          {selectedTemplate.schedule.frequency === 'custom' && 'По расписанию'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Статистика:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Использований: {selectedTemplate.usageCount}</p>
                    <p>Последний раз: {formatLastUsed(selectedTemplate.lastUsed)}</p>
                  </div>
                </div>

                <button
                  onClick={() => handleApplyTemplate(selectedTemplate)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <PlayIcon className="w-5 h-5" />
                  <span>Применить шаблон</span>
                </button>
              </div>
            ) : (
              <div className="text-center text-gray-500 mt-8">
                <PencilIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Выберите шаблон для просмотра деталей</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationTemplates;
